import { getApiBaseUrl } from './envService';

const ALLOWED_ROLES = new Set(['system', 'user', 'assistant']);

const toApiMessages = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    throw new Error('Invalid request format');
  }

  return messages.map((message) => {
    if (!message || !ALLOWED_ROLES.has(message.role) || typeof message.content !== 'string') {
      throw new Error('Invalid request format');
    }

    return {
      role: message.role,
      content: message.content,
    };
  });
};

const requestChat = async (messages) => {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    throw new Error('Interview server URL is missing. Set INTERVIEW_API_BASE_URL in config.js.');
  }

  const body = {
    messages: toApiMessages(messages),
  };

  const maxTokens = process.env.REACT_APP_CHAT_MAX_TOKENS;
  const temperature = process.env.REACT_APP_CHAT_TEMPERATURE;
  if (maxTokens) {
    body.max_tokens = parseInt(maxTokens, 10);
  }
  if (temperature) {
    body.temperature = parseFloat(temperature);
  }

  const response = await fetch(`${baseUrl}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Failed to get a response from the interview server.');
  }

  const content = data.choices?.[0]?.message?.content;
  if (typeof content !== 'string' || !content.trim()) {
    throw new Error('The interview server returned an empty response.');
  }

  return content;
};

export const sendMessageToGPT = async (messages) => {
  try {
    return await requestChat(messages);
  } catch (error) {
    console.error('Error calling interview server:', error);
    throw error;
  }
};

export const sendTechnicalInterviewMessage = async (messages) => {
  return sendMessageToGPT(messages);
};

export const sendBehavioralInterviewMessage = async (messages) => {
  return sendMessageToGPT(messages);
};

export const createInterviewerSystemMessage = () => {
  return {
    role: "system",
    content: `You are a professional behavioral interviewer conducting a job interview. Keep your responses concise with short sentences. Your role is to assess the candidate's qualifications, experience, and fit for the position.

Guidelines:
1. Ask one question at a time and wait for the candidate's response.
2. Start with a brief introduction.
3. Focus on behavioral questions about specific experiences.
4. Examples:
   - "Tell me about a challenge at work. How did you handle it?"
   - "Describe a time you worked in a team to solve a problem."
   - "When did you adapt to a significant change?"
5. Ask follow-up questions when needed.
6. Tailor questions based on their resume/job description.
7. Be professional but friendly.
8. Keep your feedback brief.
9. Ask 5-7 questions total.
10. When ending the interview, say "INTERVIEW COMPLETE" at the start of your final message.
11. In your final message, provide a letter grade (A+, A, B, C, D, or F) based on their overall performance.
12. Format your final evaluation as: "Your interview score: [GRADE]" at the end of your message.

Begin by introducing yourself briefly, then ask your first question.`
  };
};

export const createTechnicalInterviewerSystemMessage = () => {
  return {
    role: "system",
    content: `You are a Senior Software Engineer conducting a technical interview. Your role is to assess the candidate's technical knowledge, problem-solving abilities, and coding skills based on the provided technologies and requirements.

Guidelines:
1. Ask one technical question at a time and wait for the candidate's response.
2. Start with a brief introduction mentioning you're a Senior Software Engineer.
3. VARY your question formats and approaches - avoid repetitive patterns.
4. Focus on diverse technical questions covering:
   - Core concepts and fundamentals
   - Problem-solving scenarios
   - Code design and architecture
   - Best practices and patterns
   - Real-world implementation challenges
   - Performance and scalability considerations
   - Security and debugging scenarios
5. Examples of varied question types (mix these up):
   - "Explain how [technology] handles [concept]"
   - "How would you implement [feature] using [technology stack]?"
   - "What are the trade-offs between [approach A] and [approach B]?"
   - "Walk me through how you'd debug [specific problem]"
   - "How would you optimize [performance scenario]?"
   - "What would happen if [unexpected scenario] occurred?"
   - "Design a system that [specific requirement]"
   - "How would you handle [edge case or failure scenario]?"
6. IMPORTANT: Make each question unique and creative. Avoid asking similar questions or using the same sentence structure repeatedly.
7. Ask follow-up questions to dive deeper into their understanding.
8. Tailor questions based on the provided tech stack and job requirements.
9. Be professional but engaging - simulate a real senior engineer conversation.
10. Keep your feedback constructive and specific.
11. Ask 6-8 technical questions total, increasing in complexity and varying in style.
12. When ending the interview, say "TECHNICAL INTERVIEW COMPLETE" at the start of your final message.
13. In your final message, provide a letter grade (A+, A, B, C, D, or F) based on their technical knowledge and problem-solving ability.
14. Format your final evaluation as: "Your technical interview score: [GRADE]" at the end of your message.
15. Include brief feedback on strengths and areas for improvement.

CREATIVITY REMINDERS:
- Use different question formats each time
- Vary the complexity and approach
- Include scenario-based questions
- Mix theoretical and practical questions
- Ask about both common and edge cases
- Include design and architecture questions

Begin by introducing yourself as a Senior Software Engineer, then ask your first technical question.`
  };
};

export const createInterviewerLegacyMessage = createInterviewerSystemMessage;
export const createInterviewProSystemMessage = createInterviewerSystemMessage;
